/**
 * useCommandPalette Hook
 *
 * Command palette state, fuzzy search, keyboard shortcut registration.
 */

import { useState, useCallback, useEffect, useMemo } from 'react';

export interface Command {
  id: string;
  label: string;
  category: 'search' | 'compose' | 'navigate' | 'action' | 'ai';
  shortcut?: string;
  action: () => void;
}

function fuzzyMatch(query: string, text: string): boolean {
  const lower = text.toLowerCase();
  const q = query.toLowerCase();
  let qi = 0;
  for (let i = 0; i < lower.length && qi < q.length; i++) {
    if (lower[i] === q[qi]) qi++;
  }
  return qi === q.length;
}

export function useCommandPalette(commands: Command[]) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  const filtered = useMemo(() => {
    if (!query) return commands;
    return commands.filter((c) => fuzzyMatch(query, c.label));
  }, [commands, query]);

  const toggle = useCallback(() => {
    setOpen((prev) => {
      if (!prev) setQuery('');
      return !prev;
    });
  }, []);

  const execute = useCallback(
    (commandId: string) => {
      const cmd = commands.find((c) => c.id === commandId);
      if (cmd) {
        cmd.action();
        setOpen(false);
        setQuery('');
      }
    },
    [commands],
  );

  // Register Cmd+K / Ctrl+K
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        toggle();
      }
      if (e.key === 'Escape' && open) {
        setOpen(false);
      }
    }
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [open, toggle]);

  return {
    open,
    setOpen,
    query,
    setQuery,
    filtered,
    toggle,
    execute,
  };
}
